import { motion } from 'framer-motion';
import { HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    id: 'faq-1',
    question: 'What areas do you serve?',
    answer: 'We serve Bridgeport, WV and surrounding areas. Fuel charges may apply to services outside of a 25 mile radius.',
  },
  {
    id: 'faq-2',
    question: 'How do I schedule a service?',
    answer: 'You can book online through our Booking page, selecting your preferred service, date, and time. A deposit is required to hold your appointment spot. We\'ll confirm your appointment within 24 hours.',
  },
  {
    id: 'faq-3',
    question: 'What are your business hours?',
    answer: 'We operate Monday through Friday, 9:00 AM to 5:00 PM. Weekend services are available by special request. After hours and emergency services are available at additional fees.',
  },
  {
    id: 'faq-4',
    question: 'Do you provide free estimates?',
    answer: 'Yes! We offer free estimates for all services. Simply fill out our contact form or booking request, and we\'ll assess your needs and provide a detailed quote before any work begins.',
  },
  {
    id: 'faq-5',
    question: 'Are you licensed and insured?',
    answer: 'Absolutely. OmniBeast Services is fully licensed and insured. All our technicians are certified professionals with years of experience in their respective trades.',
  },
  {
    id: 'faq-6',
    question: 'What payment methods do you accept?',
    answer: 'We accept Cash, Credit and Debit Cards (fees apply), Check, and E-Payment. Payment is typically due upon completion of the service.',
  },
  {
    id: 'faq-7',
    question: 'Do you offer emergency services?',
    answer: 'Yes, we provide after hours and emergency services for urgent repairs. Contact us immediately, and we\'ll do our best to dispatch a technician as soon as possible. Additional fees apply for after-hours and emergency calls.',
  },
  {
    id: 'faq-8',
    question: 'What\'s your cancellation policy?',
    answer: 'Cancellations must be made or rescheduled 24 hours in advance or the deposit will be forfeited, unless the cancellation or rescheduling is requested by us.',
  },
  {
    id: 'faq-9',
    question: 'Do you offer service contracts?',
    answer: 'Yes! We offer contracts for ongoing services. When a contract is in place, each cleaning is slightly discounted if the monthly payment is prepaid. Contact us to set up a contract tailored to your schedule and needs.',
  },
  {
    id: 'faq-10',
    question: 'Do you offer Non-Disclosure Agreements (NDAs)?',
    answer: 'Absolutely. We offer Non-Disclosure Agreements to anyone who may ask. Your privacy and confidentiality are important to us — just let us know and we\'ll provide an NDA before any work begins.',
  },
];

export const FAQSection = () => {
  return (
    <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="faq-section">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
            <HelpCircle className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-sm text-gray-300">Got Questions?</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Find answers to common questions about our services, scheduling, and policies.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="glass rounded-2xl p-6 md:p-10"
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="border-[#1E293B] last:border-0"
                data-testid={`faq-item-${index + 1}`}
              >
                <AccordionTrigger className="text-white hover:text-[#00F0FF] hover:no-underline text-left text-base md:text-lg font-medium py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Still have questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-gray-400 mb-4">Still have questions?</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 text-[#00F0FF] hover:gap-3 transition-all font-medium"
            data-testid="faq-contact-link"
          >
            Contact our team
            <span className="text-lg">→</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
